import { apiRequest } from './api';
import api from './api';
import {
  ApiResponse,
  Brand,
  BrandFormData,
  Model,
  ModelFormData,
  Equipment,
  EquipmentFormData,
  EquipmentFilters,
  EquipmentStats
} from '../types';

export interface EquipmentOperationResult {
  success: boolean;
  message: string;
  data?: any;
  error?: string;
  debug_result?: string;
}

import { env } from '../utils/env';

class EquipmentService {
  // ===== MARCAS =====

  // Listar todas as marcas
  async getBrands(): Promise<ApiResponse<Brand[]>> {
    return await apiRequest.get<Brand[]>('/brands');
  }

  // Obter uma marca específica
  async getBrand(id: number): Promise<ApiResponse<Brand>> {
    return await apiRequest.get<Brand>(`/brands/${id}`);
  }

  // Criar nova marca
  async createBrand(data: BrandFormData): Promise<ApiResponse<Brand>> {
    return await apiRequest.post<Brand>('/brands', data);
  }

  // Atualizar marca
  async updateBrand(id: number, data: BrandFormData): Promise<ApiResponse<Brand>> {
    return await apiRequest.put<Brand>(`/brands/${id}`, data);
  }

  // Deletar marca
  async deleteBrand(id: number): Promise<ApiResponse> {
    return await apiRequest.delete(`/brands/${id}`);
  }

  // ===== MODELOS =====

  // Listar modelos com filtros
  async getModels(filters: { marcaId?: number; tipo?: string } = {}): Promise<ApiResponse<Model[]>> {
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });

    return await apiRequest.get<Model[]>(`/models?${params.toString()}`);
  }

  // Obter um modelo específico
  async getModel(id: number): Promise<ApiResponse<Model>> {
    return await apiRequest.get<Model>(`/models/${id}`);
  }

  // Criar novo modelo
  async createModel(data: ModelFormData): Promise<ApiResponse<Model>> {
    const formData = new FormData();

    // Adicionar dados do modelo
    formData.append('marcaId', data.marcaId.toString());
    formData.append('titulo', data.titulo);
    formData.append('tipo', data.tipo);
    formData.append('portas_lan', data.portas_lan.toString());

    if (data.frequencia_wifi) {
      formData.append('frequencia_wifi', data.frequencia_wifi);
    }

    if (data.portas_fxs) {
      formData.append('portas_fxs', data.portas_fxs.toString());
    }

    // Adicionar imagem se fornecida
    if (data.imagem) {
      formData.append('imagem', data.imagem);
    }

    return await apiRequest.post<Model>('/models', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  }

  // Atualizar modelo
  async updateModel(id: number, data: Partial<ModelFormData>): Promise<ApiResponse<Model>> {
    const formData = new FormData();

    // Adicionar apenas os campos que foram alterados
    if (data.marcaId !== undefined) {
      formData.append('marcaId', data.marcaId.toString());
    }
    if (data.titulo) {
      formData.append('titulo', data.titulo);
    }
    if (data.tipo) {
      formData.append('tipo', data.tipo);
    }
    if (data.portas_lan !== undefined) {
      formData.append('portas_lan', data.portas_lan.toString());
    }
    if (data.frequencia_wifi) {
      formData.append('frequencia_wifi', data.frequencia_wifi);
    }
    if (data.portas_fxs !== undefined) {
      formData.append('portas_fxs', data.portas_fxs.toString());
    }
    if (data.imagem) {
      formData.append('imagem', data.imagem);
    }

    return await apiRequest.put<Model>(`/models/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  }

  // Deletar modelo
  async deleteModel(id: number): Promise<ApiResponse> {
    return await apiRequest.delete(`/models/${id}`);
  }

  // ===== EQUIPAMENTOS =====

  // Listar equipamentos com filtros e paginação
  async getEquipments(filters: EquipmentFilters & { page?: number; limit?: number } = {}): Promise<ApiResponse<Equipment[]> & { stats?: EquipmentStats }> {
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });

    const response = await apiRequest.get<Equipment[]>(`/equipments?${params.toString()}`);
    return response as ApiResponse<Equipment[]> & { stats?: EquipmentStats };
  }

  // Obter equipamentos de um contrato específico
  async getEquipmentsByContract(contractId: number): Promise<ApiResponse<Equipment[]>> {
    return await apiRequest.get<Equipment[]>(`/equipments/contract/${contractId}`);
  }

  // Obter um equipamento específico
  async getEquipment(id: number): Promise<ApiResponse<Equipment>> {
    return await apiRequest.get<Equipment>(`/equipments/${id}`);
  }

  // Criar novo equipamento
  async createEquipment(data: EquipmentFormData): Promise<ApiResponse<Equipment>> {
    return await apiRequest.post<Equipment>('/equipments', data);
  }

  // Atualizar equipamento
  async updateEquipment(id: number, data: Partial<EquipmentFormData>): Promise<ApiResponse<Equipment>> {
    return await apiRequest.put<Equipment>(`/equipments/${id}`, data);
  }

  // Deletar equipamento
  async deleteEquipment(id: number): Promise<ApiResponse> {
    return await apiRequest.delete(`/equipments/${id}`);
  }

  // Devolver equipamento (remover vinculação com contrato)
  async devolverEquipamento(id: number): Promise<ApiResponse<Equipment>> {
    return await apiRequest.post<Equipment>(`/equipments/${id}/devolver`);
  }

  // Transferir equipamento para outro contrato
  async transferirEquipmento(equipmentId: number, newContractId: number, contratoServicoId: number): Promise<ApiResponse<Equipment>> {

    try {
      const response = await api.put(`/equipments/${equipmentId}/transferir`, {
        novo_contrato_id: newContractId,
        contrato_servico_id: contratoServicoId
      }) as ApiResponse<Equipment>;
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao transferir equipamento');
    }
  }

  // Obter estatísticas
  async getStats(filters: EquipmentFilters = {}): Promise<ApiResponse<EquipmentStats>> {
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });

    const queryString = params.toString();
    const url = queryString ? `/equipments/stats?${queryString}` : '/equipments/stats';

    return await apiRequest.get<EquipmentStats>(url);
  }

  // ===== OPERAÇÕES DE EQUIPAMENTOS =====

  /**
   * Provisionar equipamento na OLT
   * Segue o fluxo: Equipamento → CTO → PON Port → OLT
   */
  async provisionarEquipamento(equipmentId: number, profileId: number, provisionName?: string): Promise<EquipmentOperationResult> {
    try {
      console.log('🚀 EquipmentService - Iniciando provisionamento do equipamento:', equipmentId);

      // O interceptor já retorna response.data, então response já é a resposta da API
      const response = await api.post(`/equipments/${equipmentId}/provisionar`, {
        perfilId: profileId,
        provisionName: provisionName
      }) as any;

      console.log('📊 EquipmentService - Resposta completa:', response);
      console.log('📊 EquipmentService - Success:', response.success);
      console.log('📊 EquipmentService - Message:', response.message);
      console.log('📊 EquipmentService - Error:', response.error);
      console.log('📊 EquipmentService - Data:', response.data);

      if (response.success) {
        const result = {
          success: true,
          message: response.message || 'Equipamento provisionado com sucesso',
          data: response.data,
          debug_result: response.debug_result
        };
        console.log('✅ EquipmentService - Retornando sucesso:', result);
        return result;
      } else {
        const result = {
          success: false,
          message: response.message || 'Erro no provisionamento',
          error: response.error || response.message || 'Erro desconhecido',
          debug_result: response.debug_result
        };
        console.log('❌ EquipmentService - Retornando erro:', result);
        return result;
      }
    } catch (error: any) {
      console.error('❌ EquipmentService - Erro na requisição de provisionamento:', error);
      const result: EquipmentOperationResult = {
        success: false,
        message: 'Erro ao provisionar equipamento',
        error: error.response?.data?.error || error.response?.data?.message || error.message || 'Erro desconhecido',
        debug_result: error.response?.data?.debug_result
      };
      console.log('❌ EquipmentService - Retornando erro de exceção:', result);
      return result;
    }
  }

  /**
   * Desprovisionar equipamento da OLT
   * Segue o fluxo: Equipamento → CTO → PON Port → OLT
   */
  async desprovisionarEquipamento(equipmentId: number): Promise<EquipmentOperationResult> {
    try {
      console.log('🚀 EquipmentService - Iniciando desprovisionamento do equipamento:', equipmentId);

      const response = await api.post(`/equipments/${equipmentId}/desprovisionar`) as any;

      console.log('📊 EquipmentService - Resposta completa:', response);
      console.log('📊 EquipmentService - Success:', response.success);
      console.log('📊 EquipmentService - Message:', response.message);
      console.log('📊 EquipmentService - Error:', response.error);
      console.log('📊 EquipmentService - Data:', response.data);

      if (response.success) {
        const result = {
          success: true,
          message: response.message || 'Equipamento desprovisionado com sucesso',
          data: response.data,
          debug_result: response.debug_result
        };
        console.log('✅ EquipmentService - Retornando sucesso:', result);
        return result;
      } else {
        const result = {
          success: false,
          message: response.message || 'Erro no desprovisionamento',
          error: response.error || response.message || 'Erro desconhecido',
          debug_result: response.debug_result
        };
        console.log('❌ EquipmentService - Retornando erro:', result);
        return result;
      }
    } catch (error: any) {
      console.error('❌ EquipmentService - Erro na requisição de desprovisionamento:', error);
      console.error('❌ EquipmentService - Tipo do erro:', typeof error);
      console.error('❌ EquipmentService - Error response:', error.response);
      console.error('❌ EquipmentService - Error response data:', error.response?.data);
      console.error('❌ EquipmentService - Error message:', error.message);

      const result: EquipmentOperationResult = {
        success: false,
        message: 'Erro ao desprovisionar equipamento',
        error: error.response?.data?.error || error.response?.data?.message || error.message || 'Erro desconhecido',
        debug_result: error.response?.data?.debug_result
      };

      console.log('❌ EquipmentService - Retornando erro de exceção:', result);
      return result;
    }
  }

  /**
   * Trocar CTO do equipamento (caixa, porta e circuito)
   */
  async trocarCto(
    equipmentId: number,
    data: {
      caixa_id: number;
      caixa_port_number: number;
      circuito_id?: number;
      circuito_identificacao?: string;
    }
  ): Promise<EquipmentOperationResult> {
    try {
      const response = await api.post(`/equipments/${equipmentId}/trocar-cto`, data) as any;

      if (response.success) {
        return {
          success: true,
          message: response.message || 'CTO trocada com sucesso',
          data: response.data,
          debug_result: response.debug_result
        };
      }

      return {
        success: false,
        message: response.message || 'Erro ao trocar CTO',
        error: response.error || response.message,
        debug_result: response.debug_result
      };
    } catch (error: any) {
      return {
        success: false,
        message: 'Erro ao trocar CTO',
        error: error.response?.data?.error || error.response?.data?.message || error.message
      };
    }
  }

  /**
   * Diagnosticar ONU
   */
  async diagnosticarONU(equipmentId: number): Promise<EquipmentOperationResult> {
    try {
      const response = await api.post(`/equipments/${equipmentId}/diagnosticar-onu`) as any;
      return {
        success: true,
        message: 'ONU diagnosticada com sucesso',
        data: response.data,
        debug_result: response.debug_result
      };
    } catch (error: any) {
      return {
        success: false,
        message: 'Erro ao diagnosticar ONU',
        error: error.response?.data?.message || error.message
      };
    }
  }

  /**
   * Checar sinal do equipamento
   */
  async checarSinalEquipamento(equipmentId: number): Promise<EquipmentOperationResult> {
    try {
      const response = await api.post(`/equipments/${equipmentId}/checar-sinal`) as any;
      return {
        success: true,
        message: 'Sinal verificado com sucesso',
        data: response.data,
        error: response.error,
        debug_result: response.debug_result
      };
    } catch (error: any) {
      return {
        success: false,
        message: 'Erro ao verificar sinal',
        error: error.response?.data?.message || error.message,
        debug_result: error.response?.data?.debug_result
      };
    }
  }



  /**
   * Reiniciar equipamento
   */
  async reiniciarEquipamento(equipmentId: number): Promise<EquipmentOperationResult> {
    try {
      const response = await api.post(`/equipments/${equipmentId}/reboot`);
      return {
        success: true,
        message: 'Equipamento reiniciado com sucesso',
        data: response.data
      };
    } catch (error: any) {
      return {
        success: false,
        message: 'Erro ao reiniciar equipamento',
        error: error.response?.data?.message || error.message
      };
    }
  }

  /**
   * Obter informações detalhadas do equipamento
   */
  async getEquipmentDetails(equipmentId: number): Promise<EquipmentOperationResult> {
    try {
      const response = await api.get(`/equipments/${equipmentId}/details`);
      return {
        success: true,
        message: 'Informações obtidas com sucesso',
        data: response.data
      };
    } catch (error: any) {
      return {
        success: false,
        message: 'Erro ao obter informações do equipamento',
        error: error.response?.data?.message || error.message
      };
    }
  }

  // ===== REGISTROS DE EQUIPAMENTOS (SAÍDA / DEVOLUÇÃO) =====

  async lancarSaida(data: {
    data_registro: string;
    funcionario_id: number;
    equipamento_ids: number[];
    observacoes?: string;
  }): Promise<ApiResponse<any>> {
    return await apiRequest.post<any>('/equipamentos-registros/saida', data);
  }

  async lancarDevolucao(data: {
    data_registro: string;
    funcionario_id: number;
    equipamento_ids: number[];
    observacoes?: string;
  }): Promise<ApiResponse<any>> {
    return await apiRequest.post<any>('/equipamentos-registros/devolucao', data);
  }

  async getEquipamentosPorFuncionario(funcionarioId: number): Promise<ApiResponse<Equipment[]>> {
    return await apiRequest.get<Equipment[]>(`/equipamentos-registros/funcionario/${funcionarioId}/equipamentos`);
  }

  async listarRegistros(filters: {
    page?: number;
    limit?: number;
    funcionario_id?: number;
    tipo?: string;
    data_inicio?: string;
    data_fim?: string;
  } = {}): Promise<ApiResponse<any>> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });
    return await apiRequest.get<any>(`/equipamentos-registros?${params.toString()}`);
  }

  // ===== CONFIGURAÇÕES =====
  async getConfiguration(chave: string): Promise<ApiResponse<any>> {
    return await apiRequest.get<any>(`/configurations/${chave}`);
  }

  async updateConfiguration(chave: string, valor: string): Promise<ApiResponse<any>> {
    return await apiRequest.put<any>(`/configurations/${chave}`, { valor });
  }

  async createConfiguration(data: { chave: string; valor: string; tipo?: string; categoria?: string }): Promise<ApiResponse<any>> {
    return await apiRequest.post<any>('/configurations', data);
  }

  // ===== MÉTODOS AUXILIARES =====

  getTipoLabel(tipo: string): string {
    const labels: Record<string, string> = {
      'onu': 'ONU',
      'ont': 'ONT',
      'roteador': 'Roteador',
      'switch': 'Switch'
    };
    return labels[tipo] || tipo;
  }

  getModalidadeLabel(modalidade: string): string {
    const labels: Record<string, string> = {
      'proprio': 'Próprio',
      'comodato': 'Comodato'
    };
    return labels[modalidade] || modalidade;
  }

  getModalidadeColor(modalidade: string): 'primary' | 'secondary' {
    const colors: Record<string, 'primary' | 'secondary'> = {
      'proprio': 'primary',
      'comodato': 'secondary'
    };
    return colors[modalidade] || 'primary';
  }

  getStatusUsoLabel(status_uso: string): string {
    const labels: Record<string, string> = {
      'livre': 'Livre',
      'em_uso': 'Em Uso'
    };

    return labels[status_uso] || status_uso;
  }

  getStatusUsoColor(status_uso: string): 'success' | 'warning' | 'error' | 'info' {
    const statusColors: Record<string, 'success' | 'info'> = {
      'livre': 'success',
      'em_uso': 'info'
    };

    return statusColors[status_uso] || 'warning';
  }

  // Verificar se GPON SN é obrigatório baseado no tipo
  isGponSnRequired(tipo: string): boolean {
    const tipoLower = tipo.toLowerCase();
    return tipoLower === 'onu' || tipoLower === 'ont';
  }

  // Formatar número de série
  formatSerial(nserie: string): string {
    return nserie.toUpperCase();
  }

  // Formatar MAC address
  formatMac(mac: string): string {
    return mac.toUpperCase().replace(/[^A-F0-9]/g, '').replace(/(.{2})/g, '$1:').slice(0, -1);
  }

  // Validar MAC address
  validateMac(mac: string): boolean {
    const macRegex = /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/;
    return macRegex.test(mac);
  }

  // Obter URL da imagem do modelo
  getModelImageUrl(imagePath?: string): string {
    if (!imagePath) {
      return '/images/no-image.png'; // Imagem padrão
    }

    // Se já for uma URL completa, retornar como está
    if (imagePath.startsWith('http')) {
      return imagePath;
    }

    // Construir URL baseada no caminho do backend
    return `${env.API_URL}/uploads/equipamentos/${imagePath}`;
  }
}

const equipmentServiceInstance = new EquipmentService();

const equipmentService = {
  // Marcas
  getBrands: equipmentServiceInstance.getBrands.bind(equipmentServiceInstance),
  getBrand: equipmentServiceInstance.getBrand.bind(equipmentServiceInstance),
  createBrand: equipmentServiceInstance.createBrand.bind(equipmentServiceInstance),
  updateBrand: equipmentServiceInstance.updateBrand.bind(equipmentServiceInstance),
  deleteBrand: equipmentServiceInstance.deleteBrand.bind(equipmentServiceInstance),

  // Modelos
  getModels: equipmentServiceInstance.getModels.bind(equipmentServiceInstance),
  getModel: equipmentServiceInstance.getModel.bind(equipmentServiceInstance),
  createModel: equipmentServiceInstance.createModel.bind(equipmentServiceInstance),
  updateModel: equipmentServiceInstance.updateModel.bind(equipmentServiceInstance),
  deleteModel: equipmentServiceInstance.deleteModel.bind(equipmentServiceInstance),

  // Equipamentos
  getEquipments: equipmentServiceInstance.getEquipments.bind(equipmentServiceInstance),
  getEquipmentsByContract: equipmentServiceInstance.getEquipmentsByContract.bind(equipmentServiceInstance),
  getEquipment: equipmentServiceInstance.getEquipment.bind(equipmentServiceInstance),
  createEquipment: equipmentServiceInstance.createEquipment.bind(equipmentServiceInstance),
  updateEquipment: equipmentServiceInstance.updateEquipment.bind(equipmentServiceInstance),
  deleteEquipment: equipmentServiceInstance.deleteEquipment.bind(equipmentServiceInstance),
  devolverEquipamento: equipmentServiceInstance.devolverEquipamento.bind(equipmentServiceInstance),
  transferirEquipmento: equipmentServiceInstance.transferirEquipmento.bind(equipmentServiceInstance),
  getStats: equipmentServiceInstance.getStats.bind(equipmentServiceInstance),

  // Operações de Equipamentos
  provisionarEquipamento: equipmentServiceInstance.provisionarEquipamento.bind(equipmentServiceInstance),
  desprovisionarEquipamento: equipmentServiceInstance.desprovisionarEquipamento.bind(equipmentServiceInstance),
  trocarCto: equipmentServiceInstance.trocarCto.bind(equipmentServiceInstance),
  diagnosticarONU: equipmentServiceInstance.diagnosticarONU.bind(equipmentServiceInstance),
  checarSinalEquipamento: equipmentServiceInstance.checarSinalEquipamento.bind(equipmentServiceInstance),
  reiniciarEquipamento: equipmentServiceInstance.reiniciarEquipamento.bind(equipmentServiceInstance),
  getEquipmentDetails: equipmentServiceInstance.getEquipmentDetails.bind(equipmentServiceInstance),

  // Registros de Equipamentos (Saída / Devolução)
  lancarSaida: equipmentServiceInstance.lancarSaida.bind(equipmentServiceInstance),
  lancarDevolucao: equipmentServiceInstance.lancarDevolucao.bind(equipmentServiceInstance),
  getEquipamentosPorFuncionario: equipmentServiceInstance.getEquipamentosPorFuncionario.bind(equipmentServiceInstance),
  listarRegistros: equipmentServiceInstance.listarRegistros.bind(equipmentServiceInstance),

  // Métodos auxiliares
  getTipoLabel: equipmentServiceInstance.getTipoLabel.bind(equipmentServiceInstance),
  getModalidadeLabel: equipmentServiceInstance.getModalidadeLabel.bind(equipmentServiceInstance),
  getModalidadeColor: equipmentServiceInstance.getModalidadeColor.bind(equipmentServiceInstance),
  getStatusUsoLabel: equipmentServiceInstance.getStatusUsoLabel.bind(equipmentServiceInstance),
  getStatusUsoColor: equipmentServiceInstance.getStatusUsoColor.bind(equipmentServiceInstance),
  isGponSnRequired: equipmentServiceInstance.isGponSnRequired.bind(equipmentServiceInstance),
  formatSerial: equipmentServiceInstance.formatSerial.bind(equipmentServiceInstance),
  formatMac: equipmentServiceInstance.formatMac.bind(equipmentServiceInstance),
  validateMac: equipmentServiceInstance.validateMac.bind(equipmentServiceInstance),
  getModelImageUrl: equipmentServiceInstance.getModelImageUrl.bind(equipmentServiceInstance),

  // Configurações
  getConfiguration: equipmentServiceInstance.getConfiguration.bind(equipmentServiceInstance),
  updateConfiguration: equipmentServiceInstance.updateConfiguration.bind(equipmentServiceInstance),
  createConfiguration: equipmentServiceInstance.createConfiguration.bind(equipmentServiceInstance)
};

export default equipmentService; 